import React, { useState, useEffect } from 'react';
import {
  Bell,
  AlertTriangle,
  Activity,
  Settings,
  Play,
  CheckCircle2,
  XCircle,
  Search,
  Plus,
  RefreshCw,
  Zap,
  TrendingUp,
  Microscope,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { NEU } from '../neuStyles';

const API = window.zignagenixAPI;

export default function AIAlertsPage({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [alerts, setAlerts] = useState([]);
  const [alertConfigs, setAlertConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showNewConfigForm, setShowNewConfigForm] = useState(false);
  const [newConfig, setNewConfig] = useState({
    alert_type: 'pubmed',
    name: '',
    threshold: 70,
    conditions: '[]',
    notify_roles: ['admin', 'supervisor'],
  });

  // Load initial data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (activeTab === 'alerts') {
      loadAlerts();
    }
  }, [filterType, filterSeverity, filterStatus]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsData, alertsData, configsData] = await Promise.all([
        API.getAlertStats(),
        API.getAlerts({ limit: 10 }),
        API.getAlertConfigs(),
      ]);
      setStats(statsData);
      setAlerts(alertsData);
      setAlertConfigs(configsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterType) filters.alert_type = filterType;
      if (filterSeverity) filters.severity = filterSeverity;
      if (filterStatus) filters.status = filterStatus;
      filters.limit = 100;
      const data = await API.getAlerts(filters);
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunScan = async (scanType) => {
    setScanStatus({ type: scanType, running: true });
    try {
      let result;
      if (scanType === 'pubmed') {
        result = await API.scanPubMedAlerts();
      } else if (scanType === 'lab_trend') {
        result = await API.scanLabTrendAlerts();
      } else if (scanType === 'efficacy') {
        result = await API.scanEfficacyAlerts();
      } else if (scanType === 'all') {
        result = await API.scanAllAlerts();
      }
      setScanStatus({ type: scanType, running: false, result });
      setTimeout(() => {
        loadDashboardData();
        if (activeTab === 'alerts') {
          loadAlerts();
        }
      }, 1000);
    } catch (error) {
      console.error(`Scan ${scanType} failed:`, error);
      setScanStatus({ type: scanType, running: false, error: true });
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await API.acknowledgeAlert(alertId);
      setAlerts(alerts.map((a) => (a.id === alertId ? { ...a, status: 'acknowledged' } : a)));
      loadDashboardData();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await API.dismissAlert(alertId, 'User dismissed');
      setAlerts(alerts.filter((a) => a.id !== alertId));
      loadDashboardData();
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const handleToggleConfig = async (configId) => {
    try {
      await API.toggleAlertConfig(configId);
      setAlertConfigs(
        alertConfigs.map((c) => (c.id === configId ? { ...c, active: !c.active } : c))
      );
    } catch (error) {
      console.error('Failed to toggle config:', error);
    }
  };

  const handleCreateConfig = async () => {
    try {
      const parsed = JSON.parse(newConfig.conditions);
      const data = {
        alert_type: newConfig.alert_type,
        name: newConfig.name,
        threshold: newConfig.threshold,
        conditions: parsed,
        notify_roles: newConfig.notify_roles,
      };
      await API.createAlertConfig(data);
      setNewConfig({
        alert_type: 'pubmed',
        name: '',
        threshold: 70,
        conditions: '[]',
        notify_roles: ['admin', 'supervisor'],
      });
      setShowNewConfigForm(false);
      const updatedConfigs = await API.getAlertConfigs();
      setAlertConfigs(updatedConfigs);
    } catch (error) {
      console.error('Failed to create config:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' };
      case 'warning':
        return { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' };
      default:
        return { bg: '#dbeafe', text: '#0c2340', border: '#3b82f6' };
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pubmed':
        return <Bell size={16} />;
      case 'lab_trend':
        return <TrendingUp size={16} />;
      case 'efficacy':
        return <Zap size={16} />;
      default:
        return <AlertTriangle size={16} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'pubmed':
        return 'PubMed';
      case 'lab_trend':
        return 'Lab Trend';
      case 'efficacy':
        return 'Efficacy';
      default:
        return type;
    }
  };

  // ===== DASHBOARD TAB =====
  const renderDashboard = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <StatsCard
          icon={<Bell size={20} />}
          label="Unacknowledged"
          value={stats?.unacknowledgedCount || 0}
          color="#2E8B98"
        />
        <StatsCard
          icon={<AlertTriangle size={20} />}
          label="Critical"
          value={stats?.bySeverity?.critical || 0}
          color="#ef4444"
        />
        <StatsCard
          icon={<Activity size={20} />}
          label="Warning"
          value={stats?.bySeverity?.warning || 0}
          color="#f59e0b"
        />
        <StatsCard
          icon={<Eye size={20} />}
          label="Info"
          value={stats?.bySeverity?.info || 0}
          color="#3b82f6"
        />
      </div>

      {/* Scan Controls */}
      <div
        style={{
          ...NEU.card,
          padding: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
          Scan Controls
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
          <ScanButton
            icon={<Bell size={16} />}
            label="PubMed"
            onClick={() => handleRunScan('pubmed')}
            loading={scanStatus?.type === 'pubmed' && scanStatus?.running}
          />
          <ScanButton
            icon={<TrendingUp size={16} />}
            label="Lab Trends"
            onClick={() => handleRunScan('lab_trend')}
            loading={scanStatus?.type === 'lab_trend' && scanStatus?.running}
          />
          <ScanButton
            icon={<Zap size={16} />}
            label="Efficacy"
            onClick={() => handleRunScan('efficacy')}
            loading={scanStatus?.type === 'efficacy' && scanStatus?.running}
          />
          <ScanButton
            icon={<RefreshCw size={16} />}
            label="Scan All"
            onClick={() => handleRunScan('all')}
            loading={scanStatus?.type === 'all' && scanStatus?.running}
            isAllScans
          />
        </div>
        {scanStatus?.result && !scanStatus?.running && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              background: '#f0fdf4',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              color: '#166534',
              boxShadow: NEU.shadow.sm,
            }}
          >
            Scan complete: {scanStatus.result.newAlertsCount || 0} new alert(s) found
          </div>
        )}
      </div>

      {/* Recent Alerts */}
      <div
        style={{
          ...NEU.card,
          padding: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
          Recent Alerts
        </h3>
        {stats?.recentAlerts?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {stats.recentAlerts.slice(0, 5).map((alert) => (
              <AlertRow key={alert.id} alert={alert} onExpand={setExpandedAlertId} expanded={expandedAlertId === alert.id} />
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>No recent alerts</p>
        )}
      </div>
    </div>
  );

  // ===== ALERTS TAB =====
  const renderAlerts = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Filters */}
      <div
        style={{
          ...NEU.card,
          padding: '16px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
        }}
      >
        <FilterSelect
          label="Type"
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: '', label: 'All Types' },
            { value: 'pubmed', label: 'PubMed' },
            { value: 'lab_trend', label: 'Lab Trend' },
            { value: 'efficacy', label: 'Efficacy' },
          ]}
        />
        <FilterSelect
          label="Severity"
          value={filterSeverity}
          onChange={setFilterSeverity}
          options={[
            { value: '', label: 'All Severities' },
            { value: 'critical', label: 'Critical' },
            { value: 'warning', label: 'Warning' },
            { value: 'info', label: 'Info' },
          ]}
        />
        <FilterSelect
          label="Status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: '', label: 'All Status' },
            { value: 'new', label: 'New' },
            { value: 'acknowledged', label: 'Acknowledged' },
            { value: 'dismissed', label: 'Dismissed' },
          ]}
        />
      </div>

      {/* Alerts List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loading ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading alerts...</p>
        ) : alerts.length > 0 ? (
          alerts.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              expanded={expandedAlertId === alert.id}
              onExpand={setExpandedAlertId}
              onAcknowledge={handleAcknowledgeAlert}
              onDismiss={handleDismissAlert}
            />
          ))
        ) : (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>No alerts found</p>
        )}
      </div>
    </div>
  );

  // ===== CONFIGURATIONS TAB =====
  const renderConfigurations = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* New Config Form */}
      {showNewConfigForm && (
        <div
          style={{
            ...NEU.card,
            padding: '20px',
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
            Create New Alert Config
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                Alert Type
              </label>
              <select
                value={newConfig.alert_type}
                onChange={(e) => setNewConfig({ ...newConfig, alert_type: e.target.value })}
                style={{
                  ...NEU.input,
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                }}
              >
                <option value="pubmed">PubMed</option>
                <option value="lab_trend">Lab Trend</option>
                <option value="efficacy">Efficacy</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                Config Name
              </label>
              <input
                type="text"
                value={newConfig.name}
                onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                placeholder="e.g., High Risk Conditions"
                style={{
                  ...NEU.input,
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                Threshold (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={newConfig.threshold}
                onChange={(e) => setNewConfig({ ...newConfig, threshold: parseInt(e.target.value) || 70 })}
                style={{
                  ...NEU.input,
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
              Conditions (JSON)
            </label>
            <textarea
              value={newConfig.conditions}
              onChange={(e) => setNewConfig({ ...newConfig, conditions: e.target.value })}
              placeholder='[{"field": "severity", "value": "high"}]'
              style={{
                ...NEU.input,
                width: '100%',
                padding: '8px 12px',
                fontSize: '12px',
                fontFamily: 'monospace',
                minHeight: '80px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button
              onClick={handleCreateConfig}
              style={{
                ...NEU.btnPrimary,
                padding: '10px 16px',
                fontSize: '14px',
              }}
            >
              Create Config
            </button>
            <button
              onClick={() => setShowNewConfigForm(false)}
              style={{
                ...NEU.btnGhost,
                padding: '10px 16px',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Configs List */}
      <div
        style={{
          ...NEU.card,
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
            Alert Configurations
          </h3>
          {!showNewConfigForm && (
            <button
              onClick={() => setShowNewConfigForm(true)}
              style={{
                ...NEU.btnPrimary,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                fontSize: '13px',
              }}
            >
              <Plus size={16} /> New Config
            </button>
          )}
        </div>

        {alertConfigs.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {alertConfigs.map((config) => (
              <div
                key={config.id}
                style={{
                  ...NEU.cardSm,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    {getTypeIcon(config.alert_type)}
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                      {config.name}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280', background: '#e5e7eb', padding: '2px 8px', borderRadius: '8px', boxShadow: NEU.shadow.pill }}>
                      {getTypeLabel(config.alert_type)}
                    </span>
                  </div>
                  <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                    Threshold: {config.threshold}% | Roles: {config.notify_roles?.join(', ') || 'none'}
                  </p>
                </div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={config.active}
                    onChange={() => handleToggleConfig(config.id)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {config.active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>No alert configurations</p>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Bell size={28} style={{ color: '#2E8B98' }} />
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
              AI Alerts
            </h1>
          </div>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Monitor and manage system alerts from PubMed, lab trends, and efficacy tracking
          </p>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            borderBottom: 'none',
            boxShadow: `inset 0 -1px 0 rgba(186,199,216,0.45)`,
          }}
        >
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Activity },
            { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
            { id: 'configurations', label: 'Configurations', icon: Settings },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  color: activeTab === tab.id ? '#2E8B98' : '#6b7280',
                  borderBottom: activeTab === tab.id ? '2px solid #2E8B98' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'alerts' && renderAlerts()}
          {activeTab === 'configurations' && renderConfigurations()}
        </div>
      </div>
    </div>
  );
}

// ===== COMPONENTS =====

function StatsCard({ icon, label, value, color }) {
  return (
    <div
      style={{
        ...NEU.card,
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '12px',
          background: color + '20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
          boxShadow: NEU.shadow.insetSm,
        }}
      >
        {icon}
      </div>
      <div>
        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
          {label}
        </p>
        <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
          {value}
        </p>
      </div>
    </div>
  );
}

function ScanButton({ icon, label, onClick, loading, isAllScans }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '10px 14px',
        background: loading
          ? '#F8F9FA'
          : isAllScans
            ? 'linear-gradient(145deg, #2E8B98, #007a96)'
            : '#F8F9FA',
        color: loading ? '#9ca3af' : isAllScans ? 'white' : '#1f2937',
        border: 'none',
        borderRadius: '12px',
        boxShadow: isAllScans ? NEU.shadow.xs : NEU.shadow.xs,
        fontSize: '13px',
        fontWeight: '500',
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
      }}
    >
      {loading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : icon}
      {label}
    </button>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...NEU.input,
          width: '100%',
          padding: '8px 12px',
          fontSize: '13px',
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function AlertRow({ alert, expanded, onExpand, onAcknowledge, onDismiss }) {
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' };
      case 'warning':
        return { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' };
      default:
        return { bg: '#dbeafe', text: '#0c2340', border: '#3b82f6' };
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pubmed':
        return <Bell size={14} />;
      case 'lab_trend':
        return <TrendingUp size={14} />;
      case 'efficacy':
        return <Zap size={14} />;
      default:
        return <AlertTriangle size={14} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'pubmed':
        return 'PubMed';
      case 'lab_trend':
        return 'Lab Trend';
      case 'efficacy':
        return 'Efficacy';
      default:
        return type;
    }
  };

  const colors = getSeverityColor(alert.severity);

  return (
    <div
      style={{
        ...NEU.card,
        overflow: 'hidden',
        transition: 'all 0.2s',
        borderRadius: 12,
      }}
    >
      <div
        onClick={() => onExpand(expanded ? null : alert.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px',
          cursor: 'pointer',
          background: expanded ? NEU.bg : NEU.bg,
          borderBottom: expanded ? 'none' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <div
            style={{
              background: colors.bg,
              color: colors.text,
              padding: '4px 8px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '600',
              border: 'none',
              boxShadow: NEU.shadow.pill,
            }}
          >
            {alert.severity?.toUpperCase()}
          </div>

          <div
            style={{
              background: '#e5e7eb',
              color: '#374151',
              padding: '2px 8px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: NEU.shadow.pill,
            }}
          >
            {getTypeIcon(alert.alert_type)}
            {getTypeLabel(alert.alert_type)}
          </div>

          <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', flex: 1 }}>
            {alert.title}
          </span>

          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            {new Date(alert.created_at).toLocaleDateString()}
          </span>
        </div>

        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {expanded && (
        <div style={{ padding: '16px', background: NEU.bg, borderTop: 'none', boxShadow: `inset 0 1px 0 rgba(186,199,216,0.45)` }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
            {alert.description}
          </p>

          {alert.source_url && (
            <p style={{ margin: '0 0 12px 0', fontSize: '12px' }}>
              <a
                href={alert.source_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2E8B98', textDecoration: 'none', fontWeight: '500' }}
              >
                View Source →
              </a>
            </p>
          )}

          {onAcknowledge && onDismiss && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {alert.status !== 'acknowledged' && (
                <button
                  onClick={() => onAcknowledge(alert.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    background: '#dcfce7',
                    color: '#166534',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    boxShadow: NEU.shadow.sm,
                  }}
                >
                  <CheckCircle2 size={14} /> Acknowledge
                </button>
              )}
              <button
                onClick={() => onDismiss(alert.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: NEU.shadow.sm,
                }}
              >
                <XCircle size={14} /> Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
